import React, { useEffect, useState, lazy, Suspense } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../../helpers";
import { SurveyResponseView } from "./survey-response-view";
import { FTRFilterPane } from "./FTRFilterPane";
import { SurveyActionView } from "./survey-actions-view";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useIsMobile } from "../../custom-hooks";
import { AnalyticalSummary } from "./analytical-summary";
import { RenderFilters, SurveySummary } from "./survey-summary";
import { LoadingState, ProjectHeader, SideOverlay } from "../../components";
import { FTPBreadCrumbs } from "../../components-new";
import { PageShell } from "../../layouts/PageShell";
import { IconButton } from "@mui/material";
import SettingsIcon from "@mui/icons-material/Settings";
import { SurveyTableSettings } from "./survey-summary/survey-table-settings";

const FieldTeamReportMaps = lazy(() => import("./survey-summary/FieldTeamReportMap"));

const AllSurveys = lazy(() => import("../survey-management/survey-summary/all-surveys"));

const FieldTeamReport = observer((props: any) => {
    const { surveyResponseStore, projectStore, userStore } = useStore();
    const { projectid, surveyid } = useParams<{
        projectid: string;
        surveyid?: string;
    }>();
    const [query, setQuery] = useState("");
    const [allSurveys, setAllSurveys] = useState([]);
    const isMobile = useIsMobile();
    const {
        new_filters,
        activeSurveyId,
        searchHighlights,
        activeSurveyResponseId,
        allSurveyResponses,
        allSurveyResponsesSummary,
        allTags,
        analytical_data,
    } = surveyResponseStore;
    const isFieldWorker = userStore.is_field_worker();
    const hasPermission = projectStore.getPermission("view_analytical_summary", false);

    const [t] = useTranslation(["survey_management", "new_voter_registration", "analytical_summary"]);

    const crumbs = [
        { name: "DashBoard", href: `/project/${projectid}`, current: true },
        { name: "Field Team Report", href: "#", current: true },
    ];

    useEffect(() => {
        getFullReport();
    }, [activeSurveyId, searchHighlights, query, new_filters, projectid]);

    useEffect(() => {
        getSurveyList();
    }, [projectid]);

    const getSurveyList = () => {
        try {
            // Read surveys directly from localStorage instead of making API call
            const activeProjectJson = localStorage.getItem("__active_project");

            if (!activeProjectJson) {
                console.error("Active project not found in localStorage");
                return;
            }

            const activeProject = JSON.parse(activeProjectJson);
            const surveys = activeProject?.surveys || [];

            if (surveys.length === 0) {
                console.warn("No surveys found in active project");
                return;
            }

            // Sort surveys by ID in descending order
            const sorted_surveys = _.sortBy(surveys, [(x) => x.id]).reverse();
            setAllSurveys(sorted_surveys);

            // Set active survey: use surveyid from URL if provided, otherwise use the first survey
            const activeSurveyId = surveyid ? parseInt(surveyid) : parseInt(sorted_surveys[0].id);

            surveyResponseStore.setActiveSurveyId(activeSurveyId);
        } catch (error) {
            console.error("Failed to get surveys from localStorage:", error);
        } finally {
            surveyResponseStore.loading = false;
        }
    };

    const getFullReport = async () => {
        const payload = {
            projectid: parseInt(projectid),
            surveyid: activeSurveyId,
            search: query,
            only_highlights: searchHighlights,
        };
        return await surveyResponseStore.getFieldReportV3(payload);
    };

    const set_query = (query) => {
        setQuery(query);
    };

    const fpProps = {
        surveys: allSurveys,
        onSearch: set_query,
        highlights: searchHighlights,
        tags: allTags,
    };

    const has_permission = () => {
        if (isFieldWorker) return false;
        else if (userStore.isRoot() || userStore.isClient()) {
            return true;
        } else {
            return hasPermission;
        }
    };

    return (
        <>
            {!isMobile ? <ProjectHeader /> : null}
            <PageShell
                title="Field Team Report"
                breadcrumbs={!isMobile ? <FTPBreadCrumbs crumbs={crumbs} /> : null}
                // headerActions={
                //     <IconButton
                //         onClick={() =>
                //             surveyResponseStore.setShowTableSettingsModal(true)
                //         }
                //     >
                //         <SettingsIcon />
                //     </IconButton>
                // }
                headerActions={
                    userStore.isRoot() ? (
                        <IconButton onClick={() => surveyResponseStore.setShowTableSettingsModal(true)}>
                            <SettingsIcon />
                        </IconButton>
                    ) : null
                }
            >
                {surveyResponseStore.loading ? <LoadingState /> : null}
                {allSurveys ? <FTRFilterPane {...fpProps} /> : null}
                {allSurveyResponses ? (
                    <>
                        {/*<div className="block text-lg font-medium  font-bold uppercase">{t("survey_summary")}</div>*/}
                        {/*<SurveySummaryView/>*/}
                        <RenderFilters />
                        {!_.isEmpty(allSurveyResponsesSummary) && <SurveySummary />}
                        {analytical_data && has_permission() && <AnalyticalSummary />}
                        {!isFieldWorker && (
                            <Suspense fallback={<p>Loading...</p>}>
                                <AllSurveys />
                            </Suspense>
                        )}
                        {activeSurveyResponseId ? <SurveyResponseView /> : null}
                    </>
                ) : (
                    <div className="py-4 text-2xl font-bold">
                        {t("surveys_not_found", { ns: "analytical_summary" })}
                        ....
                    </div>
                )}
                <Suspense fallback={<p>Loading...</p>}>
                    <FieldTeamReportMaps />
                </Suspense>

                <SurveyActionView />
            </PageShell>

            <SideOverlay
                show={surveyResponseStore.showTableSettingsModal}
                onClose={() => surveyResponseStore.setShowTableSettingsModal(false)}
                title="Table Settings"
                size="5xl"
            >
                <SurveyTableSettings />
            </SideOverlay>
        </>
    );
});

export default FieldTeamReport;
